import { createContext, useContext, useMemo } from 'react';
import { getEnv, Env } from '@slc/tools/utils/getEnv';
import { AppEnvironment } from '@repo/types/tenant';
import { TenantConfig } from '@repo/types';

export type GlobalContext = AppEnvironment & {
  env: Env;
  apiUrl: string;
};

export const Context = createContext<GlobalContext>({
  env: Env.PRODUCTION,
  apiUrl: '',
  assetUrl: '',
  imageUrl: '',
  googleMapsApiKey: '',
  siteName: '',
  siteType: 'league',
  entityName: '',
  twitterSite: '',
  recaptchaSiteKey: '',
  pwaDismissDays: 7,
});

export const useGlobalContext = () => {
  return useContext(Context);
};

interface GlobalContextProviderProps {
  children: React.ReactNode;
  tenantConfig: TenantConfig | undefined;
  isLoading: boolean;
}

export const GlobalContextProvider = ({ children, tenantConfig, isLoading }: GlobalContextProviderProps) => {
  const env = getEnv();

  const apiUrl: string = useMemo(() => {
    // During SSR, import.meta.env.VITE_API_URL is a build-time constant (e.g., "http://localhost:3002")
    // which differs from the runtime API URL. Use tenantConfig.env.apiUrl instead, which server.mjs
    // already sets to process.env.VITE_API_URL at runtime. This ensures query keys match between
    // SSR prefetch and component hooks, enabling React Query hydration.
    if (
      typeof window !== 'undefined' &&
      typeof import.meta.env.VITE_API_URL === 'string' &&
      import.meta.env.VITE_API_URL.length > 0
    ) {
      return import.meta.env.VITE_API_URL;
    }

    return tenantConfig?.env?.apiUrl ?? '';
  }, [tenantConfig]);

  // Wait for tenant config to load (only needed for client-only rendering without SSR)
  if (isLoading || !tenantConfig?.env) {
    return null;
  }

  const ctx = {
    ...tenantConfig.env,
    env,
    apiUrl,
  };

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export const getGlobalContext = (tenantEnv: AppEnvironment): GlobalContext => {
  const env = getEnv();

  // During SSR, use tenantConfig.env.apiUrl (set by server.mjs from process.env.VITE_API_URL).
  // import.meta.env.VITE_API_URL is a build-time constant that doesn't match the runtime URL.
  let apiUrl: string = tenantEnv.apiUrl;
  if (
    typeof window !== 'undefined' &&
    typeof import.meta.env.VITE_API_URL === 'string' &&
    import.meta.env.VITE_API_URL.length > 0
  ) {
    apiUrl = import.meta.env.VITE_API_URL;
  }

  return {
    ...tenantEnv,
    env,
    apiUrl,
  };
};
